// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getPageLinkByNodeId, getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import {
    ExcludeFromNavigation,
    ExcludeFromSignInRequired,
    HideFooter,
    HideFromSearchEngines,
    HideFromSiteSearch,
    HideHeader,
    HorizontalRule,
    LinkFieldValue,
    MetaDescription,
    MetaKeywords,
    OpenGraphImage,
    OpenGraphTitle,
    OpenGraphUrl,
} from "@insite/client-framework/Types/FieldDefinition";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Page from "@insite/mobius/Page";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { BlogInfoSection } from "@vanmeter/App/Components/Blogs/BlogCard";
import XngZone from "@vanmeter/App/Components/Common/XngZone";
import Notify from "@vanmeter/App/Components/Notify";
import loadBlog from "@vanmeter/App/Handlers/BlogsList/LoadBlog";
import loadTopLevelCategories from "@vanmeter/App/Handlers/Brands/loadTopLevelCategories";
import { IBlogDetailsModel } from "@vanmeter/App/Services/BlogListService";
import Link, { LinkPresentationProps } from "@vanmeter/MobiusOverrides/Link/Link";
import { fontConfig } from "@vanmeter/XDL/constants";
import { IVMICategoryModel } from "@vanmeter/XDL/types";
import { color, Divider, styledScrollbar, xScreen } from "@vanmeter/XDL/Utilities";
import React, { useEffect } from "react";
import { css } from "styled-components";

const enum fields {
    title = "title",
    summary = "summary",
    imagePublicId = "imagePublicId",
    imageAltText = "imageAltText",
    blogType = "blogType",
    pageTargets = "pageTargets",
    categoryTargets = "categoryTargets",
    brandTargets = "brandTargets",
    showCategoriesOption = "showCategoriesOption",
    publishDateTimeOffset = "publishDateTimeOffset",
    useDefaultTemplate = "useDefaultTemplate",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.summary]: string;
        [fields.imagePublicId]: string;
        [fields.imageAltText]: string;
        [fields.blogType]: string;
        [fields.pageTargets]: { fields: { page: LinkFieldValue } }[];
        [fields.categoryTargets]: { fields: { category: string } }[];
        [fields.brandTargets]: { fields: { brand: string } }[];
        [fields.showCategoriesOption]: boolean;
        [fields.publishDateTimeOffset]: string;
        [fields.useDefaultTemplate]: boolean;
    };
}

type Props = PageProps & OwnProps;

export interface VmiBlogPageStyles {
    page?: InjectableCss;
    headerWrapper?: InjectableCss;
    titleStyle?: TypographyPresentationProps;
    categoriesTitle?: TypographyPresentationProps;
    categoryLink?: LinkPresentationProps;
    linksWrapper?: InjectableCss;
}

const styles: VmiBlogPageStyles = {
    page: {
        css: css`
            display: grid;
            grid-template-columns: 1fr 317px;
            gap: 22px;

            ${xScreen("lte", "md")} {
                grid-template-columns: 1fr 1fr;
            }
            ${xScreen("lte", "sm")} {
                grid-template-columns: unset;
            }
        `,
    },
    headerWrapper: {
        css: css`
            .info-wrapper {
                padding: 0;
            }
        `,
    },
    titleStyle: { variant: "h1" },
    categoriesTitle: {
        variant: "h4",
        css: css`
            padding-bottom: 12px;
            border-bottom: 1px solid ${color("common.border")};
            margin-bottom: 20px;
        `,
    },
    linksWrapper: {
        css: css`
            height: 196px;
            max-height: 196px;
            display: flex;
            flex-direction: column;
            gap: 16px;
            margin-bottom: 24px;
            ${styledScrollbar()}
        `,
    },
    categoryLink: {
        variant: "quarternary" as any,
        typographyProps: {
            ...fontConfig.smallSingleLine,
        },
    },
};

const VmiBlogDetailsPage: React.FunctionComponent<Props> = ({ id, fields }) => {
    const dispatch = useAppDispatch();
    const isEditing = useShellContext().isEditing;
    const blogListPageUrl = useAppSelector(state => getPageLinkByPageType(state, "VmiBlogListPage")?.url);
    const [categories, setCategories] = React.useState<IVMICategoryModel[]>([]);
    const [blogDetails, setBlogDetails] = React.useState<IBlogDetailsModel>();

    useEffect(() => {
        if (isEditing) {
            return;
        }
        dispatch(
            loadBlog({
                pageId: id,
                onSuccess: blogDetails => {
                    dispatch({ type: "xBlogDetailsPage/SetBlog", blogId: id, blog: blogDetails });
                    setBlogDetails(blogDetails);
                },
            }),
        );
    }, []);

    useEffect(() => {
        if (isEditing) {
            setBlogDetails({
                title: fields.title,
                summary: fields.summary,
                image: fields.imagePublicId,
                imageAltText: fields.imageAltText,
                type: fields.blogType,
                categories: fields.categoryTargets.map(x => x.fields.category),
                brands: fields.brandTargets.map(x => x.fields.brand),
                showCategoriesOption: fields.showCategoriesOption,
                publishDate: fields.publishDateTimeOffset,
            });
        }
    }, [fields, isEditing]);

    useEffect(() => {
        if (blogDetails?.showCategoriesOption) {
            dispatch(
                loadTopLevelCategories({
                    onSuccess: result => {
                        setCategories(result);
                    },
                }),
            );
        }
    }, [blogDetails?.showCategoriesOption]);

    if (!fields.useDefaultTemplate) {
        return (
            <Page>
                <XngZone contentId={id} zoneName="BlogContent" hint="Blog Content" />
            </Page>
        );
    }

    return (
        <Page {...styles.page}>
            <StyledWrapper {...styles.headerWrapper}>
                <Typography {...styles.titleStyle}>{translate(blogDetails?.title ?? "")}</Typography>
                <BlogInfoSection
                    blogDate={blogDetails?.publishDate}
                    blogType={blogDetails?.type}
                    categories={blogDetails?.categories}
                />
                <Divider margin="12px 0 24px" direction="horizontal" />
                {isEditing && <Notify type="info">{siteMessage("VMI_Apply_Indexing_Message")}</Notify>}
                <XngZone contentId={id} zoneName="BlogContent" hint="Blog Content" />
            </StyledWrapper>
            <StyledWrapper>
                {blogDetails?.showCategoriesOption && categories && (
                    <>
                        <Typography {...styles.categoriesTitle}>{translate("News Categories")}</Typography>
                        <StyledWrapper {...styles.linksWrapper}>
                            {categories.map(category => (
                                <Link
                                    key={category.id}
                                    href={`${blogListPageUrl}?category=${category.name}`}
                                    aria-label={category.name}
                                    {...styles.categoryLink}
                                >
                                    {category.name}
                                </Link>
                            ))}
                        </StyledWrapper>
                    </>
                )}
                <XngZone contentId={id} zoneName="AdditionalContent" hint="Additional Content" />
            </StyledWrapper>
        </Page>
    );
};
const pageModule: PageModule = {
    component: VmiBlogDetailsPage,
    definition: {
        hasEditableUrlSegment: true,
        hasEditableTitle: true,
        pageType: "Content",
        allowedParents: ["VmiBlogListPage", "VmiFolderPage"],
        fieldDefinitions: [
            ExcludeFromNavigation,
            ExcludeFromSignInRequired,
            HideFooter,
            HideFromSearchEngines,
            HideFromSiteSearch,
            HideHeader,
            HorizontalRule,
            MetaDescription,
            MetaKeywords,
            OpenGraphTitle,
            OpenGraphUrl,
            OpenGraphImage,
            {
                name: "horizontalRule",
                defaultValue: "",
                editorTemplate: "HorizontalRule",
                fieldType: "General",
            },
            {
                name: fields.title,
                defaultValue: "",
                editorTemplate: "TextField",
                fieldType: "General",
                isRequired: true,
            },

            {
                name: fields.summary,
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "General",
                isRequired: true,
            },

            {
                name: "horizontalRule",
                defaultValue: "",
                editorTemplate: "HorizontalRule",
                fieldType: "General",
            },
            {
                name: fields.imagePublicId,
                displayName: "Image",
                defaultValue: "",
                editorTemplate: "ImagePickerField",
                fieldType: "General",
                isRequired: true,
            },
            {
                name: fields.imageAltText,
                displayName: "Image Alt Text",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: "horizontalRule",
                defaultValue: "",
                editorTemplate: "HorizontalRule",
                fieldType: "General",
            },
            {
                name: fields.pageTargets,
                editorTemplate: "ListField",
                fieldType: "General",
                defaultValue: [],
                getDisplay: (item: HasFields, state) => {
                    const {
                        url: { type, value },
                    } = item.fields;
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return link?.title;
                    }
                    return value;
                },
                fieldDefinitions: [
                    {
                        name: "url",
                        editorTemplate: "LinkField",
                        isRequired: true,
                        allowCategories: () => false,
                        allowUrls: () => false,
                        defaultValue: {
                            type: "Page",
                            value: "",
                        },
                    },
                ],
            },
            {
                name: fields.brandTargets,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.brand;
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "brand",
                        editorTemplate: "BlogEventsFieldEditor" as any,
                        isRequired: true,
                        defaultValue: "",
                    },
                ],
            },
            {
                name: fields.categoryTargets,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.category;
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "category",
                        editorTemplate: "BlogEventsFieldEditor" as any,
                        isRequired: true,
                        defaultValue: "",
                    },
                ],
            },
            {
                name: "horizontalRule",
                defaultValue: "",
                editorTemplate: "HorizontalRule",
                fieldType: "General",
            },
            {
                name: fields.blogType,
                editorTemplate: "SystemListDropDownField",
                systemListName: "BlogType",
                defaultValue: "",
                isRequired: true,
                fieldType: "General",
            },
            {
                name: fields.publishDateTimeOffset,
                defaultValue: new Date(),
                editorTemplate: "DateTimeField",
                fieldType: "General",
                isRequired: true,
            },
            {
                name: fields.showCategoriesOption,
                displayName: "Show Top-Level Categories",
                defaultValue: false,
                editorTemplate: "CheckboxField",
                fieldType: "General",
            },
            {
                name: fields.useDefaultTemplate,
                displayName: "Use Default Template",
                defaultValue: true,
                editorTemplate: "CheckboxField",
                fieldType: "General",
            },
        ],
    },
};
export default pageModule;
/**
 * @deprecated Use string literal "VmiBlogDetailsPage" instead of this constant.
 */
export const VmiBlogDetailsPageContext = "VmiBlogDetailsPage";
